import type { UISchemaElement } from "@jsonforms/core";
import type { CaseFieldOption, CaseFieldValues } from "../model/caseReview";
import { RECORD_ID_PROPERTY } from "../constants/catalogRecordIdentity";
import { isCriteria, typesOf } from "./caseFieldPresentation";

/**
 * The parts of a repeatable collection that are the same whichever collection it is.
 *
 * A record's detail layout, and how a record and a collection count. None of it names Medical
 * History, Reporters or anything else: the caller passes the scope it authored and the state it
 * already holds.
 */

/** Resolves coded chip labels from the generated schema and the options in the review document. */
export function chipOptionLabel(schema: Record<string, unknown> | undefined,
                                rowOptions: Record<string, CaseFieldOption[]>) {
  return (values: CaseFieldValues) => (property: string) => {
    const declared = (schema?.properties as Record<string, Record<string, unknown>> | undefined)?.[property];
    const source = declared?.["x-codelist"];
    const value = values[property];
    if (typeof source !== "string" || typeof value !== "number") return undefined;
    return rowOptions[source]?.find((option) => option.id === value)?.label;
  };
}

interface DetailElement {
  scope?: string;
  options?: { detail?: UISchemaElement };
  elements?: DetailElement[];
}

/**
 * The layout authored for one record of a collection.
 *
 * Returns undefined rather than throwing, and never at module load: a missing detail layout is a
 * section that cannot show its records, which the caller can say — a throw while the module is
 * being imported takes the whole route down and names nothing.
 */
export function recordDetailLayout(uischema: UISchemaElement, scope: string): UISchemaElement | undefined {
  const walk = (element: DetailElement): UISchemaElement | undefined => {
    if (element.scope === scope && element.options?.detail) return element.options.detail;
    for (const child of element.elements ?? []) {
      const found = walk(child);
      if (found) return found;
    }
    return undefined;
  };
  return walk(uischema as DetailElement);
}

/**
 * The label of the authored Group that holds one control, or undefined when none does.
 *
 * <p>A child collection is authored inside a labelled Group — "Dosage Regimens", "Quality
 * Control" — and the heading belongs with the list rather than with the fields above it, so the
 * list carries the label its own Group gives it.
 */
export function groupLabelOf(uischema: UISchemaElement, scope: string): string | undefined {
  const walk = (element: DetailElement, label?: string): string | undefined => {
    if (element.scope === scope) return label;
    const own = (element as { type?: string; label?: string }).type === "Group"
      ? (element as { label?: string }).label ?? label : label;
    for (const child of element.elements ?? []) {
      const found = walk(child, own);
      if (found !== undefined) return found;
    }
    return undefined;
  };
  return walk(uischema as DetailElement);
}

/**
 * The Group labels a layout would draw as headings of its own.
 *
 * <p>A nested list borrows its Group's label, which reads well when the Group holds nothing else —
 * "Dosage Regimens" over the regimen cards. Quality Control holds five fields *and* the entries,
 * so the same label would appear twice in one card; the list falls back to its own name.
 */
export function renderedGroupLabels(uischema: UISchemaElement): Set<string> {
  const labels = new Set<string>();
  const walk = (element: DetailElement) => {
    const group = element as { type?: string; label?: string };
    if (group.type === "Group" && group.label && hasControl(element)) labels.add(group.label);
    (element.elements ?? []).forEach(walk);
  };
  walk(uischema as DetailElement);
  return labels;
}

function hasControl(element: DetailElement): boolean {
  const node = element as { type?: string; options?: { detail?: unknown } };
  if (node.type === "Control") return !node.options?.detail;
  return (element.elements ?? []).some(hasControl);
}

/**
 * A record's schema with every list removed, however deep.
 *
 * <p>A collection renders as cards, so a control over its array would render the same rows a
 * second time as raw JSON in a textarea. One level down matters as much as the top: a product's
 * quality-control object holds its entries.
 */
export function recordScalarSchema(schema: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!schema) return schema;
  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return schema;
  return {
    ...schema,
    properties: Object.fromEntries(Object.entries(properties)
      .filter(([, property]) => property.type !== "array")
      .map(([key, property]) => [key, property.type === "object" ? recordScalarSchema(property) : property])),
  };
}

/**
 * The control keys a record's schema declares, dotted the way {@link recordValues} keys them:
 * `determined_causality.method` for a member of an object, the object itself for a set of
 * true/false flags (criteria), and no child collections or record id.
 *
 * <p>Read from the schema rather than a row because a row carries an object nobody has filled in
 * yet as a single `null`, which hides every control inside it.
 */
export function recordControlKeys(schema: Record<string, unknown> | undefined, prefix = ""): string[] {
  const properties = schema?.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return [];
  return Object.entries(properties).flatMap(([key, property]) => {
    const types = typesOf(property);
    if (key === RECORD_ID_PROPERTY || types.includes("array")) return [];
    if (types.includes("object") && !isCriteria(property)) return recordControlKeys(property, `${prefix}${key}.`);
    return [`${prefix}${key}`];
  });
}

/**
 * The schema of each control a section or record declares, keyed the way
 * {@link recordControlKeys} keys it: dotted through an object that holds fields of its own, whole
 * for a field that is an object itself (criteria, or an object carrying its own field id).
 */
export function recordControlSchemas(schema: Record<string, unknown> | undefined,
                                     prefix = ""): Map<string, Record<string, unknown>> {
  const properties = schema?.properties as Record<string, Record<string, unknown>> | undefined;
  const controls = new Map<string, Record<string, unknown>>();
  Object.entries(properties ?? {}).forEach(([key, property]) => {
    const types = typesOf(property);
    if (key === RECORD_ID_PROPERTY || types.includes("array")) return;
    if (types.includes("object") && !("x-field-id" in property) && !isCriteria(property)) {
      recordControlSchemas(property, `${prefix}${key}.`)
        .forEach((control, controlKey) => controls.set(controlKey, control));
      return;
    }
    controls.set(`${prefix}${key}`, property);
  });
  return controls;
}

/** The value a dotted control key names, read through the objects that hold it. */
export function valueAt(values: Record<string, unknown>, key: string): CaseFieldValues[string] {
  return key.split(".").reduce<unknown>((current, segment) =>
    current && typeof current === "object" ? (current as Record<string, unknown>)[segment] : undefined,
    values) as CaseFieldValues[string];
}

/**
 * One record's own values, keyed the way its controls are.
 *
 * <p>A control inside an object is addressed by a dotted path — `quality_control.quantity` — so a
 * draft keyed that way needs no translation at any of the four places a key is used: the control,
 * the decision, the finding and the dirty list. Child collections are not values; they have their
 * own drafts, and leaving them here would save them twice.
 */
export function recordValues(record: Record<string, unknown>): CaseFieldValues {
  const values: CaseFieldValues = {};
  Object.entries(record).forEach(([key, value]) => {
    if (key === RECORD_ID_PROPERTY || Array.isArray(value)) return;
    if (value !== null && typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length > 0 && entries.every(([, child]) => child === null || typeof child === "boolean")) {
        values[key] = value as CaseFieldValues[string];
        return;
      }
      Object.entries(recordValues(value as Record<string, unknown>)).forEach(([nested, own]) => {
        values[`${key}.${nested}`] = own;
      });
      return;
    }
    values[key] = value as CaseFieldValues[string];
  });
  return values;
}

/** The dotted keys of a record's values, put back into the objects they name. */
export function nestRecordValues(values: Record<string, unknown>): Record<string, unknown> {
  const nested: Record<string, unknown> = {};
  Object.entries(values).forEach(([key, value]) => {
    const segments = key.split(".");
    let holder = nested;
    for (const segment of segments.slice(0, -1)) {
      holder = (holder[segment] ??= {}) as Record<string, unknown>;
    }
    holder[segments[segments.length - 1]] = value;
  });
  return nested;
}

/** The rows a record carries for one of its child collections, wherever the collection sits. */
export function childRecords(record: Record<string, unknown>, collectionId: string): Array<Record<string, unknown>> {
  const value = collectionId.split(".").reduce<unknown>(
    (node, segment) => (node as Record<string, unknown> | undefined)?.[segment], record);
  return Array.isArray(value) ? value as Array<Record<string, unknown>> : [];
}
